import { Table } from '@tiptap/extension-table'
import type { Node as PMNode } from '@tiptap/pm/model'
import { formatDelimiterRow, isExplicitWidths, parseDelimiterRow } from '../../lib/table-geometry'

/**
 * The tiptap `table` node with its column widths carried through markdown.
 *
 * WHY AN OVERRIDE. A part IS its markdown, and tiptap-markdown 0.9.0's table serializer always
 * writes the delimiter row as `| --- | --- |`, while markdown-it throws the dash counts away when it
 * parses one. So a width the user sets by dragging a border existed only in the local editor state:
 * gone on reload, never seen by a peer, reset by the next `setContent`. Widths are now stored as
 * the delimiter row's dash counts (`lib/table-geometry.ts`), and this extension is the one place
 * both directions of that live:
 *   - a `colPercents` attribute on the table node — the width set, or null for auto
 *   - a markdown-it core rule that reads the delimiter line back out of the SOURCE (through the
 *     `table_open` token's line map, since the tokens carry no dash counts) and hands it to the
 *     DOM parser as `data-col-percents`
 *   - a serializer identical to tiptap-markdown's except for the delimiter row.
 *
 * PARITY. `backend/adapters/markdown/table-markdown.ts` is the same extension for the server
 * canonicaliser — the web cannot import backend source. Both must produce the same bytes or the
 * server erases the widths on its next canonicalisation pass; `shared/md-canonical.fixture.json`
 * (the `table-width-*` corpus cases) is what proves they do.
 *
 * The Rich editor does not use this node directly: `table-resize.ts` extends it with the drag
 * handling and the percentage rendering, which are screen concerns the server never needs.
 *
 * Only the `html: false` fallback is reproduced for a table GFM cannot express (merged cells,
 * multi-paragraph cells): both stacks run tiptap-markdown with `html: false`, which writes the
 * literal `[table]` — unchanged from before.
 */

/** Minimal structural view of markdown-it — just what the parse rule touches. */
interface MdToken {
  type: string
  map: [number, number] | null
  attrSet(name: string, value: string): void
}
interface MdCoreState {
  src: string
  tokens: MdToken[]
}
interface MdIt {
  core: { ruler: { after(after: string, name: string, rule: (state: MdCoreState) => void): void } }
}

/** Minimal structural view of tiptap-markdown's serializer state. */
interface MdSerializerState {
  inTable?: boolean
  write(text: string): void
  ensureNewLine(): void
  renderInline(node: PMNode): void
  closeBlock(node: PMNode): void
}

/** The DOM attribute the parse rule hands the widths over in. */
const PERCENTS_ATTR = 'data-col-percents'

/** tiptap-markdown runs every extension's `parse.setup` before EVERY parse, on one long-lived
 *  markdown-it instance — register the rule once per instance. */
const withRule = new WeakSet<object>()

/** The widths a table should be written with: its `colPercents`, if they still fit its columns. */
export function tablePercents(node: PMNode): number[] | null {
  const percents = node.attrs.colPercents as number[] | null | undefined
  const columns = node.firstChild?.childCount ?? 0
  return isExplicitWidths(percents) && percents.length === columns ? percents : null
}

/** Read `data-col-percents="20,30,50"` back into a width set (null when absent or invalid). */
function readPercentsAttr(value: string | null): number[] | null {
  if (!value) return null
  const percents = value.split(',').map((v) => Number(v))
  return isExplicitWidths(percents) ? percents : null
}

function hasSpan(cell: PMNode): boolean {
  return (cell.attrs.colspan as number) > 1 || (cell.attrs.rowspan as number) > 1
}

/** tiptap-markdown's own test: a header row of plain header cells, then plain body cells. */
function isMarkdownSerializable(node: PMNode): boolean {
  const rows: PMNode[] = []
  node.forEach((row) => rows.push(row))
  const cellsOf = (row: PMNode): PMNode[] => {
    const cells: PMNode[] = []
    row.forEach((cell) => cells.push(cell))
    return cells
  }
  const [head, ...body] = rows
  if (!head) return false
  if (cellsOf(head).some((c) => c.type.name !== 'tableHeader' || hasSpan(c) || c.childCount > 1)) return false
  return !body.some((row) =>
    cellsOf(row).some((c) => c.type.name === 'tableHeader' || hasSpan(c) || c.childCount > 1),
  )
}

export const MarkdownTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      colPercents: {
        default: null,
        parseHTML: (element: HTMLElement) => readPercentsAttr(element.getAttribute(PERCENTS_ATTR)),
        renderHTML: (attrs: Record<string, unknown>) =>
          isExplicitWidths(attrs.colPercents as number[] | null)
            ? { [PERCENTS_ATTR]: (attrs.colPercents as number[]).join(',') }
            : {},
      },
    }
  },

  addStorage() {
    return {
      ...this.parent?.(),
      markdown: {
        serialize(this: { editor: unknown }, state: MdSerializerState, node: PMNode) {
          if (!isMarkdownSerializable(node)) {
            // tiptap-markdown's html:false fallback for a node markdown cannot express.
            console.warn(`Tiptap Markdown: "${node.type.name}" node is only available in html mode`)
            state.write(`[${node.type.name}]`)
            state.closeBlock(node)
            return
          }
          const percents = tablePercents(node)
          state.inTable = true
          node.forEach((row, _offset, i) => {
            state.write('| ')
            row.forEach((cell, _o, j) => {
              if (j) state.write(' | ')
              const content = cell.firstChild
              if (content && content.textContent.trim()) state.renderInline(content)
            })
            state.write(' |')
            state.ensureNewLine()
            if (!i) {
              state.write(formatDelimiterRow(row.childCount, percents))
              state.ensureNewLine()
            }
          })
          state.closeBlock(node)
          state.inTable = false
        },
        parse: {
          setup(markdownit: MdIt) {
            if (withRule.has(markdownit)) return
            withRule.add(markdownit)
            markdownit.core.ruler.after('block', 'niobe_table_widths', (state) => {
              let lines: string[] | null = null
              for (const token of state.tokens) {
                if (token.type !== 'table_open' || !token.map) continue
                lines ??= state.src.split('\n')
                // A GFM table's first line is the header row, its second the delimiter row.
                const percents = parseDelimiterRow(lines[token.map[0] + 1])
                if (percents) token.attrSet(PERCENTS_ATTR, percents.join(','))
              }
            })
          },
        },
      },
    }
  },
})
